import React, { useCallback, useContext } from "react";
import {
  BlockStack,
  InlineStack,
  Button,
  Text,
  ChoiceList,
  Banner,
  Box,
} from "@shopify/polaris";
import { ResourcePicker } from "@shopify/app-bridge/actions";
import { AppBridgeContext } from "../AppBridgeProvider";
import { CHECKOUT_TARGET_CHOICES } from "../../settings/checkoutTarget";
import { LabelWithTooltip } from "./LabelWithTooltip";

function handlesFromSelection(items) {
  return items
    .map((x) => x.handle)
    .filter(Boolean)
    .join(", ");
}

export default function CheckoutTargetSection({ target, onPatch, disabled = false }) {
  const app = useContext(AppBridgeContext);
  const tg = target || {};
  const mode = tg.mode || "always";
  const selectedProducts = Array.isArray(tg.selectedProducts) ? tg.selectedProducts : [];
  const selectedCollections = Array.isArray(tg.selectedCollections) ? tg.selectedCollections : [];

  const runCollectionPicker = useCallback(() => {
    if (!app) return;
    try {
      const initialSelectionIds = selectedCollections.filter((c) => c.id).map((c) => ({ id: c.id }));
      const picker = ResourcePicker.create(app, {
        resourceType: ResourcePicker.ResourceType.Collection,
        options: { selectMultiple: true, initialSelectionIds },
      });
      picker.subscribe(ResourcePicker.Action.SELECT, (payload) => {
        const selection = payload.selection || [];
        const next = selection.map((s) => ({ id: s.id, handle: s.handle, title: s.title }));
        onPatch({
          selectedCollections: next,
          collectionHandles: handlesFromSelection(next),
        });
        picker.unsubscribe();
      });
      picker.subscribe(ResourcePicker.Action.CANCEL, () => picker.unsubscribe());
      picker.dispatch(ResourcePicker.Action.OPEN);
    } catch (e) {
      console.error("[CheckoutTarget] collection picker", e);
    }
  }, [app, onPatch, selectedCollections]);

  const runProductPicker = useCallback(() => {
    if (!app) return;
    try {
      const initialSelectionIds = selectedProducts.filter((p) => p.id).map((p) => ({ id: p.id }));
      const picker = ResourcePicker.create(app, {
        resourceType: ResourcePicker.ResourceType.Product,
        options: { selectMultiple: true, showVariants: false, initialSelectionIds },
      });
      picker.subscribe(ResourcePicker.Action.SELECT, (payload) => {
        const selection = payload.selection || [];
        const next = selection.map((s) => ({ id: s.id, handle: s.handle, title: s.title }));
        onPatch({
          selectedProducts: next,
          productHandles: handlesFromSelection(next),
        });
        picker.unsubscribe();
      });
      picker.subscribe(ResourcePicker.Action.CANCEL, () => picker.unsubscribe());
      picker.dispatch(ResourcePicker.Action.OPEN);
    } catch (e) {
      console.error("[CheckoutTarget] product picker", e);
    }
  }, [app, onPatch, selectedProducts]);

  const removeCollection = (index) => {
    const next = selectedCollections.filter((_, i) => i !== index);
    onPatch({
      selectedCollections: next,
      collectionHandles: handlesFromSelection(next),
    });
  };

  const removeProduct = (index) => {
    const next = selectedProducts.filter((_, i) => i !== index);
    onPatch({
      selectedProducts: next,
      productHandles: handlesFromSelection(next),
    });
  };

  return (
    <BlockStack gap="400">
      {!app ? (
        <Banner tone="warning" title="Resource picker needs Shopify Admin">
          Open this app embedded in Shopify Admin (with <code>host</code> in the URL) to pick collections and
          products.
        </Banner>
      ) : null}

      <ChoiceList
        title="Target"
        choices={CHECKOUT_TARGET_CHOICES.map((c) => ({
          label: <LabelWithTooltip label={c.label} tooltip={c.helpText} />,
          value: c.value,
        }))}
        selected={[mode]}
        onChange={(next) => onPatch({ mode: next[0] || "always" })}
      />

      {mode === "collections" ? (
        <BlockStack gap="300">
          <Button onClick={runCollectionPicker} disabled={!app}>
            Browse collections
          </Button>
          {selectedCollections.length === 0 ? (
            <Text as="p" variant="bodySm" tone="subdued">
              No collections selected.
            </Text>
          ) : (
            <BlockStack gap="200">
              {selectedCollections.map((c, i) => (
                <Box key={c.id || `${c.handle}-${i}`} padding="200" background="bg-surface-secondary" borderRadius="200">
                  <InlineStack align="space-between" blockAlign="center" wrap={false} gap="200">
                    <BlockStack gap="050">
                      <Text as="span" variant="bodyMd" fontWeight="semibold">
                        {c.title || c.handle || "Collection"}
                      </Text>
                      {c.handle ? (
                        <Text as="span" variant="bodySm" tone="subdued">
                          /{c.handle}
                        </Text>
                      ) : null}
                    </BlockStack>
                    <Button tone="critical" variant="plain" disabled={disabled} onClick={() => removeCollection(i)}>
                      Remove
                    </Button>
                  </InlineStack>
                </Box>
              ))}
            </BlockStack>
          )}
        </BlockStack>
      ) : null}

      {mode === "products" ? (
        <BlockStack gap="300">
          <Button onClick={runProductPicker} disabled={!app || disabled}>
            Browse products
          </Button>
          {selectedProducts.length === 0 ? (
            <Text as="p" variant="bodySm" tone="subdued">
              No products selected.
            </Text>
          ) : (
            <BlockStack gap="200">
              {selectedProducts.map((p, i) => (
                <Box key={p.id || `${p.handle}-${i}`} padding="200" background="bg-surface-secondary" borderRadius="200">
                  <InlineStack align="space-between" blockAlign="center" wrap={false} gap="200">
                    <BlockStack gap="050">
                      <Text as="span" variant="bodyMd" fontWeight="semibold">
                        {p.title || p.handle || "Product"}
                      </Text>
                      {p.handle ? (
                        <Text as="span" variant="bodySm" tone="subdued">
                          /products/{p.handle}
                        </Text>
                      ) : null}
                    </BlockStack>
                    <Button tone="critical" variant="plain" disabled={disabled} onClick={() => removeProduct(i)}>
                      Remove
                    </Button>
                  </InlineStack>
                </Box>
              ))}
            </BlockStack>
          )}
        </BlockStack>
      ) : null}
    </BlockStack>
  );
}
